import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { FC, useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "../../../components";
import { DataTable } from "../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../components/data-table/DeleteActionCell";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import { USER_GROUPS_QUERY_KEY } from "../../../hooks/useAllUserGroupsForACompany";
import { useLocalization } from "../../../hooks/useLocalization";
import { Routes } from "../../../types/NavigationPaths";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../../experiences/hooks/useUsersForReleaseChannel";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../../users/hooks/useUserExperiences";
import { USER_GROUP_QUERY_KEY } from "../hooks/useUserGroupById";
import { USERS_IN_GROUP_QUERY_KEY } from "../hooks/useUsersInGroup";
import { AddUserToGroupDialog } from "./AddUserToGroupDialog";

interface UsersTabProps {
  users?: UserManagement_V1.UserWithCompanyName[];
  companyId?: string;
  groupId?: string;
  userGroupName?: string;
  isPrimaryGroup?: boolean;
  isLoading?: boolean;
}

export interface UserRow {
  userId: string;
  name: string;
  emailAddress: string;
}

const mapUserToRow = (
  user: UserManagement_V1.UserWithCompanyName,
): UserRow => ({
  userId: user.userId,
  name: `${user.surname}, ${user.forename}`,
  emailAddress: user.emailAddress,
});

const renderName = (ctx: CellContext<UserRow, any>) => {
  return <span style={{ fontWeight: 500 }}>{ctx.getValue<string>()}</span>;
};

const INITIAL_SORTING: SortingState = [{ id: "name", desc: false }];

export const UsersTab: FC<UsersTabProps> = ({
  users = [],
  companyId,
  groupId,
  isPrimaryGroup,
  userGroupName,
  isLoading,
}) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const userManagementClient = useUserManagementServiceV1();

  const handleRowClick = (user: UserRow) => {
    void navigate(`/companies/${companyId}${Routes.USERS}/${user.userId}`);
  };

  const [isAddGroupToUserDialogOpen, setIsAddGroupToUserDialogOpen] =
    useState(false);
  const [isRemoveUserDialogOpen, setIsRemoveUserDialogOpen] = useState(false);
  const [selectedUserToRemove, setSelectedUserToRemove] =
    useState<UserRow | null>(null);

  const queryClient = useQueryClient();

  // --- Helper for Optimistic Updates ---
  const updateCacheOptimistically = (targetUserId: string) => {
    if (!groupId) return;

    queryClient.setQueriesData<
      | InfiniteData<{ users: UserManagement_V1.UserWithCompanyName[] }>
      | UserManagement_V1.UserWithCompanyName[]
    >({ queryKey: [USERS_IN_GROUP_QUERY_KEY, groupId] }, (oldData) => {
      if (!oldData) return oldData;

      if ("pages" in oldData) {
        return {
          ...oldData,
          pages: oldData.pages.map((page) => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (page && typeof page === "object" && "users" in page) {
              return {
                ...page,
                users: page.users.filter((u) => u.userId !== targetUserId),
              };
            }

            return page;
          }),
        };
      }

      if (Array.isArray(oldData)) {
        return oldData.filter((user) => user.userId !== targetUserId);
      }

      return oldData;
    });
  };

  const removeUserFromGroupMutation = useMutation({
    mutationFn: ({
      userGroupId,
      targetUserId,
    }: {
      userGroupId: string;
      targetUserId: string;
    }) =>
      userManagementClient.removeUserFromUserGroup(userGroupId, targetUserId),
    onSuccess: (_, { targetUserId }) => {
      // PTIMISTIC UPDATE: Remove user from list instantly
      updateCacheOptimistically(targetUserId);

      // Helper for invalidation
      const invalidateQueries = () => {
        void queryClient.invalidateQueries({
          queryKey: [USERS_IN_GROUP_QUERY_KEY, groupId],
        });
        void queryClient.invalidateQueries({
          queryKey: [USER_GROUP_QUERY_KEY, groupId],
        });
        void queryClient.invalidateQueries({
          queryKey: [INCOMING_LICENSES_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [ALL_LICENSES_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY, targetUserId],
        });
        void queryClient.invalidateQueries({
          queryKey: [USER_GROUPS_QUERY_KEY, targetUserId],
        });
      };

      // Double Invalidation (Immediate + Delayed)
      invalidateQueries();
      setTimeout(invalidateQueries, 1000);

      setIsRemoveUserDialogOpen(false);
      setSelectedUserToRemove(null);
    },
  });

  const mappedUsers = useMemo(() => users.map(mapUserToRow), [users]);

  const handleRemoveFromGroup = useCallback((selectedUser: UserRow) => {
    setSelectedUserToRemove(selectedUser);
    setIsRemoveUserDialogOpen(true);
  }, []);

  const handleRemoveFromGroupCancel = () => {
    setIsRemoveUserDialogOpen(false);
    setSelectedUserToRemove(null);
    removeUserFromGroupMutation.reset();
  };

  const handleRemoveFromGroupConfirm = () => {
    if (selectedUserToRemove?.userId && groupId) {
      removeUserFromGroupMutation.mutate({
        userGroupId: groupId,
        targetUserId: selectedUserToRemove.userId,
      });
    }
  };

  const renderActionCell = (ctx: CellContext<UserRow, any>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleRemoveFromGroup(ctx.row.original);
    };

    if (isPrimaryGroup) {
      return null;
    }

    return (
      <DeleteActionCell
        actionText={t("groups.removeUser")}
        onClick={handleClick}
      />
    );
  };

  const usersColumns = useMemo<ColumnDef<UserRow, any>[]>(
    () => [
      {
        id: "name",
        header: t("groups.usersTableHeaders.name"),
        accessorKey: "name",
        cell: renderName,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: 150,
      },
      {
        accessorKey: "emailAddress",
        header: t("groups.usersTableHeaders.email"),
        enableSorting: true,
        enableColumnFilter: false,
        size: 150,
      },
      {
        id: "actions",
        header: undefined,
        cell: renderActionCell,
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [t],
  );

  return (
    <>
      <DataTable<UserRow>
        actionButtonText={t("groups.addUserToGroupDialog.addUserToGroup")}
        actionButtonTextShort={t("groups.addUserToGroupDialog.add")}
        columns={usersColumns}
        data={mappedUsers}
        defaultSort={INITIAL_SORTING}
        globalFilterPlaceholder={t("groups.searchUsers")}
        isLoading={isLoading}
        onActionClick={() => setIsAddGroupToUserDialogOpen(true)}
        onRowClick={handleRowClick}
        showActionButton={!isPrimaryGroup}
      />
      <AddUserToGroupDialog
        companyId={companyId}
        groupId={groupId}
        isOpen={isAddGroupToUserDialogOpen}
        onClose={() => setIsAddGroupToUserDialogOpen(false)}
        usersToExclude={mappedUsers}
      />
      <ConfirmationDialog.Root
        aria-describedby="remove-from-group-dialog-description"
        aria-labelledby="remove-from-group-dialog-title"
        maxWidth="xs"
        onClose={handleRemoveFromGroupCancel}
        open={isRemoveUserDialogOpen}
      >
        <ConfirmationDialog.Title
          id="remove-from-group-dialog-title"
          showIcon={true}
        >
          {t("groups.removeUser")}
        </ConfirmationDialog.Title>
        <ConfirmationDialog.Content error={removeUserFromGroupMutation.error}>
          <ConfirmationDialog.Text id="remove-from-group-dialog-description">
            <Trans
              components={{ strong: <strong /> }}
              i18nKey="groups.removeUserConfirmation"
              values={{
                userName: selectedUserToRemove?.name,
                groupName: userGroupName,
              }}
            />
          </ConfirmationDialog.Text>
        </ConfirmationDialog.Content>
        <ConfirmationDialog.Actions>
          <ConfirmationDialog.Button
            onClick={handleRemoveFromGroupCancel}
            variant="contained"
          >
            {t("groups.cancel")}
          </ConfirmationDialog.Button>
          <ConfirmationDialog.Button
            isLoading={removeUserFromGroupMutation.isPending}
            loadingText={t("groups.removing")}
            onClick={handleRemoveFromGroupConfirm}
            variant="secondary"
          >
            {t("groups.remove")}
          </ConfirmationDialog.Button>
        </ConfirmationDialog.Actions>
      </ConfirmationDialog.Root>
    </>
  );
};
